import {
  addGroupAdmin,
  approveGroupRequest,
  banGroupMember,
  rejectGroupRequest,
  removeGroupAdmin,
  removeGroupMember,
  unbanGroupMember,
} from "firebase-config";
import {
  Group,
  groupAddAdmin,
  groupApproveRequest,
  groupBanUser,
  groupRejectRequest,
  groupRemoveAdmin,
  groupRemoveUser,
  groupUnbanUser,
} from "model/Group/Group";
import { useState } from "react";
import { selectUsername } from "selectors/User";
import { FlexRow } from "styles/layout";
import {
  ActionInProgressSpinner,
  MemberActionButton,
  Username,
  UserType,
} from "./styles";
import { useAppSelector } from "app/hooks";

interface Props {
  groupId: string;
  group: Group;
  uid: string;
  initialUserType: string;
  callerType: string;
}

export const GroupMember = ({
  groupId,
  group,
  uid,
  initialUserType,
  callerType,
}: Props) => {
  const username = useAppSelector((state) => selectUsername(state, uid));
  const [userType, setUserType] = useState<string | undefined>(initialUserType);
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const actions = getActions(
    uid,
    group,
    groupId,
    callerType,
    userType,
    setUserType,
    actionInProgress,
    setActionInProgress
  );

  if (!userType) {
    return <></>;
  }

  return (
    <FlexRow>
      <Username>{username}</Username>
      <UserType>{userType}</UserType>
      {actions.length > 0 && (
        <FlexRow style={{ columnGap: "5px" }}>
          {actions}
          {actionInProgress && <ActionInProgressSpinner />}
        </FlexRow>
      )}
    </FlexRow>
  );
};

const approveAction = (
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  return approveGroupRequest({ groupId: groupId, memberId: uid }).then(() => {
    setUserType("Member");
    groupApproveRequest(group, uid);
  });
};

const rejectAction = (
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  return rejectGroupRequest({ groupId: groupId, memberId: uid }).then(() => {
    groupRejectRequest(group, uid);
    setUserType(undefined);
  });
};

const removeAction = (
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  return removeGroupMember({ groupId: groupId, memberId: uid }).then(() => {
    groupRemoveUser(group, uid);
    setUserType(undefined);
  });
};

const banAction = (
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  return banGroupMember({ groupId: groupId, memberId: uid }).then(() => {
    groupBanUser(group, uid);
    setUserType("Banned");
  });
};

const unbanAction = (
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  return unbanGroupMember({ groupId: groupId, memberId: uid }).then(() => {
    groupUnbanUser(group, uid);
    setUserType("");
  });
};

const addAdminAction = (
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  return addGroupAdmin({ groupId: groupId, memberId: uid }).then(() => {
    groupAddAdmin(group, uid);
    setUserType("Admin");
  });
};

const removeAdminAction = (
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
) => {
  return removeGroupAdmin({ groupId: groupId, memberId: uid }).then(() => {
    groupRemoveAdmin(group, uid);
    setUserType("Member");
  });
};

const userActionButton = (
  action: (
    uid: string,
    group: Group,
    groupId: string,
    setUserType: React.Dispatch<React.SetStateAction<string | undefined>>
  ) => Promise<void>,
  actionText: string,
  uid: string,
  group: Group,
  groupId: string,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>,
  actionInProgress: boolean,
  setActionInProgress: React.Dispatch<React.SetStateAction<boolean>>
) => {
  return (
    <MemberActionButton
      key={actionText}
      disabled={actionInProgress}
      onClick={() => {
        setActionInProgress(true);
        action(uid, group, groupId, setUserType)
          .catch((error) => {
            console.log(error.message);
          })
          .finally(() => {
            setActionInProgress(false);
          });
      }}
    >
      {actionText}
    </MemberActionButton>
  );
};

const getActions = (
  uid: string,
  group: Group,
  groupId: string,
  callerType: string,
  userType: string | undefined,
  setUserType: React.Dispatch<React.SetStateAction<string | undefined>>,
  actionInProgress: boolean,
  setActionInProgress: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (userType === "Banned") {
    if (callerType === "Admin" || callerType === "Owner") {
      return [
        userActionButton(
          unbanAction,
          "Unban",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
      ];
    }

    return [];
  }
  if (userType === "Request") {
    if (callerType === "Admin" || callerType === "Owner") {
      return [
        userActionButton(
          approveAction,
          "Approve",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
        userActionButton(
          rejectAction,
          "Reject",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
        userActionButton(
          banAction,
          "Ban",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
      ];
    }

    return [];
  }

  if (userType === "Member") {
    if (callerType === "Admin") {
      return [
        userActionButton(
          removeAction,
          "Remove",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
        userActionButton(
          banAction,
          "Ban",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
      ];
    }

    if (callerType === "Owner") {
      return [
        userActionButton(
          addAdminAction,
          "Add Admin",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
        userActionButton(
          removeAction,
          "Remove",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
        userActionButton(
          banAction,
          "Ban",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
      ];
    }
  }

  if (userType === "Admin") {
    if (callerType === "Owner") {
      return [
        userActionButton(
          removeAdminAction,
          "Remove Admin",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
        userActionButton(
          removeAction,
          "Remove",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
        userActionButton(
          banAction,
          "Ban",
          uid,
          group,
          groupId,
          setUserType,
          actionInProgress,
          setActionInProgress
        ),
      ];
    }
  }

  return [];
};
